import { ref } from "vue";
import type { TreeDefinitionDto, TreeNodeDto } from "~/api";
import { buildTreeNodeMap } from "~/composables/useGeoTree";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";

/**
 * Composable for loading the SectorSetup tree definition and building
 * a flat GUID → TreeNodeDto lookup map.
 */
export function useSectorTree() {
  const myInfoStore = useMyInfoStore();

  const sectorTreeData = ref<TreeDefinitionDto | null>(null);
  const flatNodeMap = ref(new Map<string, TreeNodeDto>());
  const loading = ref(false);

  const loadSectorTree = async () => {
    loading.value = true;
    try {
      await myInfoStore.load();
      sectorTreeData.value = await apiClient.tree.getTreeDefinitionByType(
        myInfoStore.currentSysOrgGuid ?? emptyGuid,
        "SectorSetup",
      );
      flatNodeMap.value.clear();
      buildTreeNodeMap(sectorTreeData.value.nodes, flatNodeMap.value);
    } catch {
      // No sector tree configured for this org
    } finally {
      loading.value = false;
    }
  };

  const getNodeName = (guid: string): string | undefined =>
    flatNodeMap.value.get(guid)?.name;

  return {
    sectorTreeData,
    flatNodeMap,
    loading,
    loadSectorTree,
    getNodeName,
  };
}
